<script setup lang="ts">
  import { reactiveOmit } from '@vueuse/core';
  import type { TabsRootEmits, TabsRootProps } from 'reka-ui';
  import { TabsRoot, useForwardPropsEmits } from 'reka-ui';
  import { provide, watch } from 'vue';
  import type { HTMLAttributes } from 'vue';
  import { useRoute } from 'vue-router';

  import { cn } from '@ui/utils/utils';

  const props = withDefaults(
    defineProps<
      TabsRootProps & {
        class?: HTMLAttributes['class'];
        withRoute?: boolean;
        variant?: 'glass' | 'underline' | 'panel';
      }
    >(),
    { variant: 'underline' },
  );
  const emits = defineEmits<TabsRootEmits>();

  const delegatedProps = reactiveOmit(props, 'class', 'variant');
  const forwarded = useForwardPropsEmits(delegatedProps, emits);

  const route = useRoute();

  watch(
    route,
    () => {
      if (props.withRoute) {
        emits('update:modelValue', route.name as string);
      }
    },
    { deep: true },
  );

  provide('tabs', {
    variant: props.variant,
  });
</script>

<template>
  <TabsRoot
    v-slot="slotProps"
    data-slot="tabs"
    v-bind="forwarded"
    :data-variant="variant"
    :class="cn('flex flex-col gap-2', props.class)"
  >
    <slot v-bind="slotProps" />
  </TabsRoot>
</template>
